// **بحثٌ عامّ في الترويسة** (قرار المالك: «أدوّر على أي حاجة نسيت مكانها فين — أبحث عن
// كشف المرتبات أو كلمة خصم إداري، يدخلني عليها على طول»).
//
// صندوقٌ واحد: تكتب، فتظهر الوجهات المطابقة بعنوانها وموضعها («الإدارة المالية ←
// مستحقات الموظفين»)، وEnter أو النقر ينقلك إليها — التبويب وقسمه الفرعي معًا.
// ولوحة المفاتيح كاملة: الأسهم للتنقّل، Esc للإغلاق، وCtrl+K أو «/» يفتحه من أي مكان.
//
// **ولا يرى المستخدم إلا ما له**: القائمة تُصفّى بالتبويبات الظاهرة له، فلا يجد بياعٌ
// «الخزنة» — كما لا يجدها في الشريط.
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { SEARCH_INDEX, searchIndex, type SearchEntry } from '../search/registry.ts';

export function GlobalSearch({ visibleTabs, canApproveRatings, onPick }: {
  visibleTabs: readonly string[];
  canApproveRatings: boolean;
  onPick: (entry: SearchEntry) => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const entries = useMemo(() => SEARCH_INDEX.filter(e =>
    visibleTabs.includes(e.tab) && (e.requires !== 'approver' || canApproveRatings)), [visibleTabs, canApproveRatings]);
  const results = useMemo(() => searchIndex(q, entries), [q, entries]);

  // Ctrl+K أو «/» (خارج حقول الكتابة) يفتح البحث من أي شاشة
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inField = (e.target as HTMLElement | null)?.closest('input, textarea, select, [contenteditable]');
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
      else if (e.key === '/' && !inField) { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // نقرةٌ خارج الصندوق تغلقه
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (e: SearchEntry) => {
    onPick(e);
    setQ(''); setOpen(false); setCursor(0);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor(c => Math.min(c + 1, Math.max(0, results.length - 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = results[cursor] ?? results[0]; if (r) pick(r); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  const showList = open && q.trim().length > 0;

  return (
    <div ref={boxRef} className="relative w-56 sm:w-64 lg:w-72" role="search" aria-label="بحث في البرنامج">
      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 start-2.5 flex items-center text-ink-400">🔍</span>
      <input ref={inputRef} type="search" value={q} aria-label="دوّر على أي شاشة"
        placeholder="دوّر على أي حاجة… (كشف المرتبات، خصم إداري)"
        className="control w-full ps-8 text-sm"
        role="combobox" aria-expanded={showList} aria-controls={listId} aria-autocomplete="list"
        onChange={e => { setQ(e.target.value); setOpen(true); setCursor(0); }}
        onFocus={() => setOpen(true)} onKeyDown={onKeyDown} />
      {showList && (
        <ul id={listId} role="listbox" aria-label="نتائج البحث"
          className="absolute end-0 z-50 mt-1 w-[22rem] max-w-[90vw] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 text-start shadow-lg">
          {results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-500">مفيش حاجة بالاسم ده — جرّب كلمة تانية (مثلًا: مرتبات، حضور، حجز).</li>
          ) : results.map((r, i) => (
            <li key={`${r.tab}:${r.section ?? ''}:${r.title}`} role="option" aria-selected={i === cursor}
              className={`cursor-pointer px-3 py-2 ${i === cursor ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
              onMouseEnter={() => setCursor(i)} onMouseDown={e => e.preventDefault()} onClick={() => pick(r)}>
              <div className="text-sm font-semibold text-ink-900">{r.title}</div>
              <div className="text-xs text-ink-500">{r.where}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
